import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { Subscription } from "rxjs";
import ConfigProps from "../../constants/configProps";
import NavigationTokens from "../../constants/navigationTokens";
import { IDisplayState } from "../../interfaces/IDisplayState";
import { ISubMenu } from "../../interfaces/ISubMenu";
import { ITabSetCategory } from "../../interfaces/ITabSetCategory";
import ConfigPropService from "../../utilities/configPropService";
import LoginUrlService from "../../utilities/loginUrlService";
import NavigationTokensService from "../../utilities/navigationTokensService";
import ScrollBarService from "../../utilities/scrollBarService";
import AccountInfo from "../accountInfo/accountInfo";
import ArrowIcon from "../arrowIcon/arrowIcon";
import { IBrandOrService } from "../brandsAndServices/brandOrService";
import UserGreeting from "../userGreeting/userGreeting";
import AccountAndPaymentsMenu from "./accountAndPaymentsMenu";
import DepthTestMenu from "./depthTestMenu";
import { variants, variantsReverse } from "./framerMotionSlideVariants";
import MainMenu from "./mainMenu";
import MegaMenuWrapper from "./megaMenuWrapper";
import MenuIds from "./MenuIds";
import MenuItem from "./menuItem";
import "./navMenu.scss";
import NavShadow from "./navShadow";
import OrdersMenu from "./ordersMenu";
import ShopByCategoryMenu from "./shopByCategoryMenu";

export interface INavMenuProps {
  isOpen: boolean;
  isMyListsOpen: boolean;
  setOpen: (value: boolean) => void;
  setMyListsOpen: (value: boolean, opt1?: boolean, opt2?: boolean) => void;
  categories: ITabSetCategory[];
  brandsAndServices: IBrandOrService[];
  className?: string;
  isLoggedIn: boolean;
  isCookied: boolean;
  firstName: string;
  customerAccountName: string;
  userConfigurations: number;
  accountNumber: string;
  displayState: IDisplayState;
  myListsUrl?: string;
  userConfigId?: string;
}

export interface INavMenuState {
  menuStack: ISubMenu[];
  subMenu: ISubMenu | null;
  loginHref: string;
  registerHref: string;
  displayAccountName: boolean;
  gsaLinkText: string;
  switchAccountHref: string;
  gsaHref: string;
  overlayMenu: string | null;
  isOpen: boolean;
  defaultSortOrderSearch: string;
}

class NavMenu extends React.Component<INavMenuProps, INavMenuState> {
  private navTokenSub: Subscription | undefined;
  private configPropSub: Subscription | undefined;
  private loginUrlSub: Subscription | undefined;

  constructor(props: INavMenuProps) {
    super(props);

    this.state = {
      menuStack: [],
      subMenu: null,
      loginHref: "",
      registerHref: "",
      displayAccountName: false,
      gsaLinkText: "",
      switchAccountHref: "",
      gsaHref: "",
      overlayMenu: "",
      isOpen: false,
      defaultSortOrderSearch: "",
    };

    this.setOpen = this.setOpen.bind(this);
    this.pushMenuStack = this.pushMenuStack.bind(this);
    this.popMenuStack = this.popMenuStack.bind(this);
    this.clearOverlayMenu = this.clearOverlayMenu.bind(this);
    this.isOverlayId = this.isOverlayId.bind(this);
  }

  componentDidMount() {
    const switchAccountUrl = LoginUrlService.getPreLoadedSwitchAccountUrl();
    this.navTokenSub = NavigationTokensService.get([
      NavigationTokens.regUserRegistration,
      NavigationTokens.wbGsa,
    ]).subscribe((navTokens) => {
      this.setState({
        registerHref: navTokens[NavigationTokens.regUserRegistration],
        switchAccountHref: switchAccountUrl,
        gsaHref: navTokens[NavigationTokens.wbGsa],
      });
    });

    this.configPropSub = ConfigPropService.get([
      ConfigProps.DisplayGsaLink,
      ConfigProps.GsaLinkText,
      ConfigProps.DisplayAccountNameOnMasterPage,
      ConfigProps.DefaultSortOrderSearch,
    ]).subscribe((configProps) => {
      this.setState({
        gsaLinkText:
          ConfigPropService.isYesOrTrue(
            configProps[ConfigProps.DisplayGsaLink]
          ) && configProps[ConfigProps.GsaLinkText]
            ? configProps[ConfigProps.GsaLinkText]
            : "",
        displayAccountName: ConfigPropService.isYesOrTrue(
          configProps[ConfigProps.DisplayAccountNameOnMasterPage]
        ),
        defaultSortOrderSearch: configProps[ConfigProps.DefaultSortOrderSearch],
      });
    });

    this.loginUrlSub = LoginUrlService.get().subscribe((loginUrl) => {
      this.setState({ loginHref: loginUrl });
    });
  }

  componentWillUnmount() {
    if (this.navTokenSub) {
      this.navTokenSub.unsubscribe();
    }

    if (this.configPropSub) {
      this.configPropSub.unsubscribe();
    }

    if (this.loginUrlSub) {
      this.loginUrlSub.unsubscribe();
    }
  }

  static getDerivedStateFromProps(
    nextProps: INavMenuProps,
    prevState: INavMenuState
  ) {
    const newState: Partial<INavMenuState> = {
      isOpen: false,
    };

    if (nextProps.isOpen) {
      ScrollBarService.applyModalOpen(true, "nav menu");
    }

    if (nextProps.isMyListsOpen) {
      ScrollBarService.applyModalOpen(true, "lists");
    }

    if (nextProps.isOpen || nextProps.isMyListsOpen) {
      newState.isOpen = true;
    } else if (
      !nextProps.isOpen &&
      !nextProps.isMyListsOpen &&
      prevState.isOpen
    ) {
      ScrollBarService.applyModalOpen(false, "nav menu");
    }

    if (!nextProps.isOpen) {
      newState.menuStack = [];
      newState.subMenu = null;
      newState.overlayMenu = "";
    }

    if (
      !nextProps.isMyListsOpen &&
      prevState.overlayMenu === MenuIds.MY_LISTS_MENU
    ) {
      newState.overlayMenu = "";
    }

    return newState;
  }

  setOpen() {
    this.props.setOpen(!this.props.isOpen);
    if (this.props.isOpen && this.state.overlayMenu) {
      this.props.setMyListsOpen(false);
    }
  }

  isOverlayId(id: string | undefined) {
    switch (id) {
      case MenuIds.MY_LISTS_MENU:
        return true;
    }

    return false;
  }

  pushMenuStack(
    id: string | undefined,
    label: string | undefined,
    icon: string | undefined,
    svg: (() => React.ReactNode) | undefined,
    src: string | undefined,
    href: string | undefined,
    subMenuUseHref: boolean | undefined,
    linkClass: string | undefined,
    extras: unknown
  ) {
    if (this.isOverlayId(id)) {
      this.props.setMyListsOpen(true, true, true);
      this.setState({
        overlayMenu: id || null,
      });
      return;
    }

    const newMenuStack = this.state.menuStack;
    const currentSubMenu = this.state.subMenu;

    const subMenu: ISubMenu = {
      key: `${id}-${newMenuStack.length + 1}`,
      id: id,
      backLabel: currentSubMenu
        ? (currentSubMenu.label as string)
        : "Main Menu",
      label: label,
      icon: icon,
      svg: svg,
      src: src,
      href: href,
      subMenuUseHref: subMenuUseHref,
      linkClass: linkClass,
      extras: extras,
    };

    newMenuStack.push(subMenu);

    this.setSubMenuAnimations(true, () => {
      this.setState({
        menuStack: newMenuStack,
        subMenu: subMenu,
      });
    });
  }

  popMenuStack() {
    const newMenuStack = this.state.menuStack;

    newMenuStack.pop();

    const subMenu =
      newMenuStack.length > 0 ? newMenuStack[newMenuStack.length - 1] : null;
    if (subMenu) {
      subMenu.reverseAnimation = true;
    }

    this.setSubMenuAnimations(false, () => {
      this.setState({
        menuStack: newMenuStack,
        subMenu: subMenu,
      });
    });
  }

  renderMenu(subMenu: ISubMenu) {
    const props: Record<string, unknown> = {};

    if (subMenu.subMenuUseHref && subMenu.href) {
      props.href = subMenu.href;
    } else {
      props.ignoreHref = true;
    }

    return (
      <div className="menu-items">
        <div className="row nav-menu-back">
          <div className="col-12">
            <button className="arrow-icon-button" onClick={this.popMenuStack}>
              <ArrowIcon />
              <span className="back-to-main-menu-label">
                {subMenu.backLabel}
              </span>
            </button>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="sub-menu-divider"></div>
          </div>
        </div>

        <MenuItem
          label={subMenu.label}
          labelClass="font-weight-bold"
          srcClass="large-menu-item-icon"
          linkClass={`${subMenu.subMenuUseHref ? "" : "cursor-default"} ${
            subMenu.linkClass
          }`}
          icon={subMenu.icon}
          svg={subMenu.svg}
          src={subMenu.src}
          {...props}
        />
        {this.renderMenuComponent(subMenu.id)}
      </div>
    );
  }

  renderMenuComponent(id: string | undefined) {
    switch (id) {
      case MenuIds.ACCOUNT_AND_PAYMENTS_MENU:
        return <AccountAndPaymentsMenu pushMenuStack={this.pushMenuStack} />;
      case MenuIds.DEPTH_TEST_MENU:
        return <DepthTestMenu pushMenuStack={this.pushMenuStack} />;
      case MenuIds.ORDERS_MENU:
        return <OrdersMenu />;
      case MenuIds.SHOP_BY_CATEGORY:
        return (
          <ShopByCategoryMenu
            pushMenuStack={this.pushMenuStack}
            categories={this.props.categories}
            brandsAndServices={this.props.brandsAndServices}
          />
        );
      case MenuIds.MEGA_MENU:
        return (
          <MegaMenuWrapper
            category={this.state.subMenu?.extras as ITabSetCategory}
            defaultSortOrderSearch={this.state.defaultSortOrderSearch}
          />
        );
      default:
        return <div>:( menu &apos;{id}&apos; is undefined</div>;
    }
  }

  setSubMenuAnimations(value: boolean, callback: () => void) {
    if (this.state.subMenu) {
      const newState: Partial<INavMenuState> = {};

      if (this.state.subMenu) {
        newState.subMenu = this.state.subMenu;
        newState.subMenu.reverseAnimation = value;
      }

      this.setState(newState as INavMenuState, callback);
    } else {
      callback();
    }
  }

  clearOverlayMenu() {
    this.props.setMyListsOpen(false);
    this.setState({
      overlayMenu: null,
    });
  }

  render() {
    return (
      <AnimatePresence>
        {this.props.isOpen && <NavShadow onClick={this.setOpen} />}
        {this.props.isOpen && !this.state.overlayMenu && (
          <motion.nav
            initial="closed"
            animate="open"
            exit="closed"
            variants={variants()}
            className={this.props.className}
            key="navmenu"
            id="nav-menu"
          >
            <div className="user-info">
              <UserGreeting
                isLoggedIn={this.props.isLoggedIn}
                isCookied={this.props.isCookied}
                firstName={this.props.firstName}
                loginHref={this.state.loginHref}
                registerHref={this.state.registerHref}
                displayRegisterLink={
                  this.props.displayState.displayRegisterLink
                }
                displayIcon={true}
              />
              <button className="arrow-icon-button" onClick={this.setOpen}>
                <ArrowIcon />
              </button>
            </div>
            <div className="account-info">
              <div className="account-center-info">
                <AccountInfo
                  isLoggedInOrCookied={
                    this.props.isLoggedIn || this.props.isCookied
                  }
                  displayAccountName={this.state.displayAccountName}
                  customerAccountName={this.props.customerAccountName}
                  hasMultipleAccounts={this.props.userConfigurations > 1}
                  switchAccountHref={this.state.switchAccountHref}
                  gsaLinkText={this.state.gsaLinkText}
                  gsaHref={this.state.gsaHref}
                  accountNumber={this.props.accountNumber}
                  mobileMenu
                  displayState={this.props.displayState}
                  userConfigId={this.props.userConfigId}
                />
              </div>
            </div>
            <div className="container">
              <AnimatePresence>
                {this.state.subMenu && (
                  <motion.div
                    key={this.state.subMenu.key}
                    initial="closed"
                    animate="open"
                    exit="closed"
                    variants={
                      this.state.subMenu.reverseAnimation
                        ? variants()
                        : variantsReverse()
                    }
                    className="menu-container"
                    style={{
                      backgroundColor: this.state.subMenu.color,
                    }}
                  >
                    {this.renderMenu(this.state.subMenu)}
                  </motion.div>
                )}
                {this.state.menuStack.length === 0 && (
                  <motion.div
                    key="mainmenu"
                    initial={this.state.subMenu ? "closed" : "open"}
                    animate="open"
                    exit="closed"
                    variants={variants()}
                    className="menu-container"
                  >
                    <MainMenu
                      pushMenuStack={this.pushMenuStack}
                      isLoggedIn={this.props.isLoggedIn}
                      displayState={this.props.displayState}
                      myListsUrl={this.props.myListsUrl}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    );
  }
}

export default NavMenu;
